import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { EVIDENCE_RELATION } from "@brainlog/shared";
import { errorMessage, get, post } from "@/lib/api";
import type { LibraryItem } from "@/lib/types";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { LibraryPicker, NotePicker } from "./pickers";
import { getEditorBridge } from "@/features/notes/editorBridge";

/** Link a claim (exact text in this note) to a source passage, record or paper page. */
export function EvidenceLinkDialog(props: { open: boolean; onOpenChange(o: boolean): void; claimNoteId: string; workspaceId: string; initialClaim?: string }) {
  const qc = useQueryClient();
  const [claim, setClaim] = useState(props.initialClaim?.trim() ?? "");
  const [sourceType, setSourceType] = useState<"note" | "paper">("note");
  const [noteSource, setNoteSource] = useState<{ id: string; title: string } | null>(null);
  const [paper, setPaper] = useState<LibraryItem | null>(null);
  const [page, setPage] = useState<number | null>(null);
  const [section, setSection] = useState("");
  const [quote, setQuote] = useState("");
  const [relation, setRelation] = useState<"supports" | "contradicts" | "context">("supports");
  const [explanation, setExplanation] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!claim) {
      const sel = getEditorBridge(props.claimNoteId)?.selection().text.trim();
      if (sel) setClaim(sel);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const pdf = paper?.attachments?.find((a) => a.mime === "application/pdf" && ["done", "partial"].includes(a.extractionStatus ?? ""));
  const pages = useQuery({
    queryKey: ["att-pages", pdf?.id],
    queryFn: () => get<{ pages: { pageNumber: number; pageLabel: string | null; charCount: number }[] }>(`/api/attachments/${pdf!.id}`),
    enabled: !!pdf,
  });
  const pageText = useQuery({
    queryKey: ["att-page", pdf?.id, page],
    queryFn: () => get<{ page: { text: string; pageLabel: string | null } }>(`/api/attachments/${pdf!.id}/pages/${page}`),
    enabled: !!pdf && !!page,
  });
  const noteText = useQuery({
    queryKey: ["note-text-preview", noteSource?.id],
    queryFn: () => get<{ note: { contentMd: string } }>(`/api/notes/${noteSource!.id}`),
    enabled: !!noteSource,
  });

  const canSave = claim.trim() && explanation.trim() && (sourceType === "note" ? !!noteSource : !!paper);

  const save = async () => {
    setBusy(true);
    try {
      await post("/api/evidence-links", {
        claimNoteId: props.claimNoteId,
        claimQuote: claim.trim(),
        relation,
        explanation: explanation.trim(),
        sourceType,
        sourceId: sourceType === "note" ? noteSource!.id : paper!.id,
        sourceAttachmentId: sourceType === "paper" && pdf && page ? pdf.id : null,
        sourcePage: sourceType === "paper" ? page : null,
        sourceSection: section.trim() || null,
        sourceQuote: quote.trim(),
      });
      toast.success("Evidence link saved with the cited versions.");
      qc.invalidateQueries({ queryKey: ["evidence", props.claimNoteId] });
      props.onOpenChange(false);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add evidence link</DialogTitle>
          <DialogDescription>
            Connect a claim in this note to the source it relies on. The current versions of both are recorded, so later edits are flagged rather than silently changing the evidence. A
            link establishes traceability; it does not prove the claim.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1">
          <Label htmlFor="ev-claim">Claim (exact text from this note)</Label>
          <Textarea id="ev-claim" rows={2} value={claim} onChange={(e) => setClaim(e.target.value)} placeholder="Select the sentence in the editor before opening this dialog, or paste it here exactly." />
        </div>
        <fieldset className="space-y-1">
          <legend className="text-sm font-medium">Source</legend>
          <div className="flex gap-4 text-sm">
            <label className="flex items-center gap-1.5">
              <input type="radio" checked={sourceType === "note"} onChange={() => setSourceType("note")} /> Research record or note
            </label>
            <label className="flex items-center gap-1.5">
              <input type="radio" checked={sourceType === "paper"} onChange={() => setSourceType("paper")} /> Paper / library item
            </label>
          </div>
        </fieldset>
        {sourceType === "note" ? (
          <div className="space-y-2">
            <NotePicker workspaceId={props.workspaceId} excludeIds={[props.claimNoteId]} selected={noteSource ? [noteSource.id] : []} onPick={(id, title) => setNoteSource({ id, title })} />
            {noteSource && noteText.data && (
              <details className="rounded border p-2 text-xs">
                <summary className="cursor-pointer">Show “{noteSource.title}” to copy an exact passage</summary>
                <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap">{noteText.data.note.contentMd}</pre>
              </details>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            <LibraryPicker workspaceId={props.workspaceId} selectedId={paper?.id} onPick={(i) => { setPaper(i); setPage(null); }} />
            {paper && pdf && (
              <div className="space-y-1">
                <Label htmlFor="ev-page">PDF page (printed page number in brackets)</Label>
                <select id="ev-page" className="h-9 w-full rounded-md border bg-background px-2 text-sm" value={page ?? ""} onChange={(e) => setPage(e.target.value ? Number(e.target.value) : null)}>
                  <option value="">No specific page</option>
                  {pages.data?.pages.map((p) => (
                    <option key={p.pageNumber} value={p.pageNumber} disabled={!p.charCount}>
                      Page {p.pageNumber}
                      {p.pageLabel ? ` [printed ${p.pageLabel}]` : ""}
                      {!p.charCount ? " — no extractable text" : ""}
                    </option>
                  ))}
                </select>
                {pageText.data && (
                  <details className="rounded border p-2 text-xs" open>
                    <summary className="cursor-pointer">Extracted text of this page</summary>
                    <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap">{pageText.data.page.text}</pre>
                  </details>
                )}
              </div>
            )}
            {paper && !pdf && <p className="text-xs text-muted-foreground">This paper has no readable PDF text. Write the section or page below.</p>}
            <div className="space-y-1">
              <Label htmlFor="ev-section">Section or page (optional)</Label>
              <Input id="ev-section" value={section} onChange={(e) => setSection(e.target.value)} placeholder="e.g. Abstract, Table 2, §3.1" />
            </div>
          </div>
        )}
        <div className="space-y-1">
          <Label htmlFor="ev-quote">Passage used (exact quotation, optional for records)</Label>
          <Textarea id="ev-quote" rows={2} value={quote} onChange={(e) => setQuote(e.target.value)} />
        </div>
        <fieldset className="space-y-1">
          <legend className="text-sm font-medium">Relation (your judgement)</legend>
          <div className="flex flex-wrap gap-4 text-sm">
            {EVIDENCE_RELATION.values.map((r) => (
              <label key={r} className="flex items-center gap-1.5">
                <input type="radio" checked={relation === r} onChange={() => setRelation(r)} /> {EVIDENCE_RELATION.labels[r]}
              </label>
            ))}
          </div>
        </fieldset>
        <div className="space-y-1">
          <Label htmlFor="ev-expl">Explanation (required)</Label>
          <Textarea id="ev-expl" rows={2} value={explanation} onChange={(e) => setExplanation(e.target.value)} placeholder="How does this source relate to the claim?" />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => props.onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={save} disabled={!canSave || busy}>
            {busy ? "Saving…" : "Save evidence link"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
